"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useMemo, useRef } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AiChartLegendContent,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
  resolveAiLegendLayout,
} from "./ai-chart";
import {
  DEFAULT_EXPORT_FORMATS,
  EMPTY_VIZ_ROWS,
  EMPTY_VIZ_SERIES,
  ensurePngExportFormats,
  formatPercentTick,
  formatValueTick,
  formatVizTooltipMetricValue,
  isRecord,
  isVizAxisReversed,
  normalizeVizExportFormats,
  resolveEffectiveXAxisTickDensity,
  resolveTimeSeriesAreaReferenceAxisId,
  resolveVizActiveDotProps,
  resolveVizAxisLineProps,
  resolveVizAxisStyle,
  resolveVizAxisTickLineProps,
  resolveVizCartesianGridProps,
  resolveVizPaletteMode,
  resolveVizReferenceAreas,
  resolveVizReferenceLabelProps,
  resolveVizReferenceLines,
  resolveVizSemanticReferenceLineProps,
  resolveVizSeriesColor,
  resolveVizTooltipMode,
  resolveXAxisTickDensityConfig,
  resolveXAxisTickLayout,
  type VizAxisDirections,
  type VizAxisPreset,
  type VizPaletteMode,
  type VizTooltipMode,
  type VizXAxisTickDensity,
} from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
} from "./dataset-fields";
import {
  formatVizAxisDateLabel,
  formatVizTooltipDomainLabel,
  isIsoDateLike,
} from "./date-format";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import { resolveDisplayLabel } from "./label-resolver";
import {
  buildTimeSeriesFinitePointCountMap,
  hasRenderableTimeSeriesValue,
  normalizeRowsForAxis,
  resolveTimeSeriesAxisOuterMargin,
  resolveTimeSeriesAxisState,
  resolveTimeSeriesNumericAxisWidth,
  resolveTimeSeriesStaticDotProps,
  resolveTooltipPercentFields,
} from "./time-series-core";
import { resolveTimeSeriesLineInterpolation } from "./time-series-line-utils";
import {
  resolveTimeSeriesChartHeight,
  TimeSeriesActionBar,
  useTimeSeriesVisibleSeries,
} from "./time-series-shared";
import { type TimeSeriesYAxisMode } from "./y-axis-mode";

export type TimeSeriesAreaProps = BaseComponentProps<{
  dataPath: string;
  xKey: string;
  series?: Array<{ key: string; label?: string | null; color?: string | null }>;
  xLabel?: string | null;
  seriesLabels?: Record<string, string> | null;
  periodLabels?: {
    current?: string | null;
    comparison?: string | null;
  } | null;
  chartHeight?: number | null;
  axisPreset?: VizAxisPreset | null;
  tooltipMode?: VizTooltipMode | null;
  paletteMode?: VizPaletteMode | null;
  yAxisMode?: TimeSeriesYAxisMode | null;
  stacking?: "grouped" | "stacked" | null;
  xAxisTicks?: { density?: VizXAxisTickDensity | null } | null;
  referenceLines?: Array<{
    value: number;
    label?: string | null;
    color?: string | null;
    axis?: "primary" | "secondary" | null;
  }> | null;
  referenceAreas?: Array<{
    from: number;
    to: number;
    label?: string | null;
    color?: string | null;
    opacity?: number | null;
    axis?: "primary" | "secondary" | null;
  }> | null;
  axisDirections?: VizAxisDirections | null;
  allowSeriesToggle?: boolean | null;
  seriesToggleMode?: "single-metric" | "multi-metric" | null;
  defaultVisibleSeriesKeys?: string[] | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
}>;

/**
 * Area の時系列可視化を描画する。
 *
 * @remarks
 * 見た目品質を AI 側だけで調整できるよう、axis/palette/tooltip の表示プリセットを
 * component props で受け取り、共通画面向け chart 実装には影響させない。
 */
export function TimeSeriesArea({ props }: TimeSeriesAreaProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const {
    dataPath,
    xKey,
    series,
    seriesLabels,
    chartHeight,
    axisPreset,
    tooltipMode,
    paletteMode,
    yAxisMode,
    stacking,
    xAxisTicks,
    referenceLines,
    referenceAreas,
    axisDirections,
    allowSeriesToggle,
    seriesToggleMode,
    defaultVisibleSeriesKeys,
    allowExport,
    exportFormats,
  } = props;
  const resolvedTooltipMode = resolveVizTooltipMode(tooltipMode);
  const resolvedPaletteMode = resolveVizPaletteMode(paletteMode);
  const rawData = useStateValue<unknown>(dataPath);
  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return EMPTY_VIZ_ROWS;
    return rawData.filter(isRecord);
  }, [rawData]);
  const seriesList = Array.isArray(series) ? series : EMPTY_VIZ_SERIES;
  const allSeriesKeys = useMemo(
    () => seriesList.map((entry) => entry.key),
    [seriesList],
  );
  const { visibleSeriesKeySet, toggleSeriesVisibility } =
    useTimeSeriesVisibleSeries({
      allSeriesKeys,
      defaultVisibleSeriesKeys,
      seriesToggleMode,
    });

  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );
  const finitePointCountBySeriesKey = useMemo(() => {
    return buildTimeSeriesFinitePointCountMap({
      rows: data,
      seriesKeys: allSeriesKeys,
    });
  }, [allSeriesKeys, data]);
  const renderableSeriesList = useMemo(() => {
    return seriesList.filter((entry) =>
      hasRenderableTimeSeriesValue(finitePointCountBySeriesKey.get(entry.key)),
    );
  }, [finitePointCountBySeriesKey, seriesList]);
  const effectiveSeriesList = useMemo(() => {
    return renderableSeriesList.filter((entry) =>
      visibleSeriesKeySet.has(entry.key),
    );
  }, [renderableSeriesList, visibleSeriesKeySet]);
  const percentKeySet = useMemo(
    () =>
      new Set(
        effectiveSeriesList
          .map((entry) => entry.key)
          .filter((key) => isPercentFieldKey(key, metaByName)),
      ),
    [effectiveSeriesList, metaByName],
  );

  const chartConfig = useMemo(() => {
    return Object.fromEntries(
      seriesList.map((entry, index) => {
        const display = resolveDisplayLabel(
          seriesLabels?.[entry.key],
          entry.label,
          metaByName.get(entry.key)?.label,
          entry.key,
        );
        return [
          entry.key,
          {
            label: display || entry.key,
            color: resolveVizSeriesColor({
              index,
              color: entry.color,
              paletteMode: resolvedPaletteMode,
            }),
          },
        ];
      }),
    );
  }, [metaByName, resolvedPaletteMode, seriesLabels, seriesList]);

  const axisState = useMemo(
    () =>
      resolveTimeSeriesAxisState({
        effectiveSeriesKeys: effectiveSeriesList.map((entry) => entry.key),
        fallbackSeriesKeys: allSeriesKeys,
        percentKeySet,
        yAxisMode,
      }),
    [allSeriesKeys, effectiveSeriesList, percentKeySet, yAxisMode],
  );
  const {
    axisSeriesKeys,
    hasPercentSeries,
    hasValueSeries,
    useDualAxis,
    useNormalizedAxis,
  } = axisState;

  const chartData = useMemo(() => {
    return normalizeRowsForAxis({
      rows: data,
      axisSeriesKeys,
      useNormalizedAxis,
    });
  }, [axisSeriesKeys, data, useNormalizedAxis]);

  const resolvedReferenceLines = useMemo(
    () => resolveVizReferenceLines(referenceLines),
    [referenceLines],
  );
  const resolvedReferenceAreas = useMemo(
    () => resolveVizReferenceAreas(referenceAreas),
    [referenceAreas],
  );
  const leftAxisSeriesKeys = useMemo(
    () => axisSeriesKeys.filter((key) => !percentKeySet.has(key)),
    [axisSeriesKeys, percentKeySet],
  );
  const rightAxisSeriesKeys = useMemo(
    () => axisSeriesKeys.filter((key) => percentKeySet.has(key)),
    [axisSeriesKeys, percentKeySet],
  );

  const xAxisTickDensity = resolveEffectiveXAxisTickDensity({
    density: xAxisTicks?.density ?? null,
    isMobile,
  });
  const xAxisTickConfig = resolveXAxisTickDensityConfig({
    density: xAxisTickDensity,
    rowCount: data.length,
    baseMinTickGap: isMobile ? 18 : 22,
  });
  const axisStyle = useMemo(
    () =>
      resolveVizAxisStyle({
        preset: axisPreset,
        isMobile,
      }),
    [axisPreset, isMobile],
  );
  const leftYAxisWidth = useMemo(
    () =>
      resolveTimeSeriesNumericAxisWidth({
        rows: chartData,
        seriesKeys: useDualAxis ? leftAxisSeriesKeys : axisSeriesKeys,
        tickFormatter:
          !useNormalizedAxis && hasPercentSeries && !hasValueSeries
            ? formatPercentTick
            : formatValueTick,
        tickFontSize: axisStyle.tickFontSize,
        isMobile,
      }),
    [
      axisSeriesKeys,
      axisStyle.tickFontSize,
      chartData,
      hasPercentSeries,
      hasValueSeries,
      isMobile,
      leftAxisSeriesKeys,
      useDualAxis,
      useNormalizedAxis,
    ],
  );
  const rightYAxisWidth = useMemo(
    () =>
      useDualAxis
        ? resolveTimeSeriesNumericAxisWidth({
            rows: chartData,
            seriesKeys: rightAxisSeriesKeys,
            tickFormatter: formatPercentTick,
            tickFontSize: axisStyle.tickFontSize,
            isMobile,
          })
        : 0,
    [
      axisStyle.tickFontSize,
      chartData,
      isMobile,
      rightAxisSeriesKeys,
      useDualAxis,
    ],
  );
  const chartRightMargin = useMemo(() => {
    if (!useDualAxis) return 16;
    return resolveTimeSeriesAxisOuterMargin({
      axisWidth: rightYAxisWidth,
      isMobile,
    });
  }, [isMobile, rightYAxisWidth, useDualAxis]);
  const isPrimaryYAxisReversed = isVizAxisReversed(axisDirections, "primary");
  const isSecondaryYAxisReversed = isVizAxisReversed(
    axisDirections,
    "secondary",
  );
  const resolveReferenceYAxisId = useCallback(
    (axis: "primary" | "secondary") => {
      return resolveTimeSeriesAreaReferenceAxisId({
        useDualAxis,
        axis,
      });
    },
    [useDualAxis],
  );
  const formatXAxisTick = useCallback(
    (value: unknown) => formatVizAxisDateLabel(String(value ?? ""), i18n),
    [i18n],
  );
  const rotateXAxis = useMemo(() => {
    return data.slice(0, 12).some((row) => {
      const raw = String(row[xKey] ?? "");
      const formatted = formatXAxisTick(row[xKey]);
      return isIsoDateLike(raw) || formatted.length > 10;
    });
  }, [data, formatXAxisTick, xKey]);
  const xAxisTickLayout = useMemo(() => {
    return resolveXAxisTickLayout({
      rotate: rotateXAxis,
      labels: data.slice(0, 12).map((row) => formatXAxisTick(row[xKey])),
    });
  }, [data, formatXAxisTick, rotateXAxis, xKey]);
  const resolvedChartHeight = resolveTimeSeriesChartHeight({
    chartHeight,
    hasDenseXAxis: rotateXAxis,
    isMobile,
  });
  const resolvedExportFormats = ensurePngExportFormats(
    normalizeVizExportFormats(exportFormats, DEFAULT_EXPORT_FORMATS),
  );
  const tooltipPercentFields = useMemo(
    () =>
      resolveTooltipPercentFields({
        axisSeriesKeys,
        percentKeySet,
        useNormalizedAxis,
      }),
    [axisSeriesKeys, percentKeySet, useNormalizedAxis],
  );
  const formatTooltipValue = useCallback(
    (value: unknown, key: string) =>
      formatVizTooltipMetricValue(value, {
        isPercent: tooltipPercentFields.includes(key),
      }),
    [tooltipPercentFields],
  );

  const handleExport = useCallback(
    async (format: "csv" | "json" | "png") => {
      const columns = [xKey, ...axisSeriesKeys];
      if (format === "csv") {
        downloadRowsAsCsv({
          rows: data,
          columns,
          baseName: "time-series-area",
        });
        return;
      }
      if (format === "json") {
        downloadRowsAsJson({
          rows: data,
          columns,
          baseName: "time-series-area",
        });
        return;
      }
      await downloadVizAsPng({
        element: containerRef.current,
        baseName: "time-series-area",
      });
    },
    [axisSeriesKeys, data, xKey],
  );
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);

  const seriesMenuItems = useMemo(
    () =>
      renderableSeriesList.map((entry) => ({
        key: entry.key,
        label:
          resolveDisplayLabel(
            seriesLabels?.[entry.key],
            entry.label,
            metaByName.get(entry.key)?.label,
            entry.key,
          ) || entry.key,
      })),
    [metaByName, renderableSeriesList, seriesLabels],
  );
  const legendPayload = useMemo(
    () =>
      renderableSeriesList.map((entry) => ({
        dataKey: entry.key,
        value:
          resolveDisplayLabel(
            chartConfig[entry.key]?.label,
            entry.label,
            metaByName.get(entry.key)?.label,
            entry.key,
          ) || entry.key,
        color: chartConfig[entry.key]?.color ?? "var(--chart-1)",
      })),
    [chartConfig, metaByName, renderableSeriesList],
  );
  const legendLayout = useMemo(
    () =>
      resolveAiLegendLayout({
        itemCount: legendPayload.length,
        isMobile,
      }),
    [isMobile, legendPayload.length],
  );

  const hasChartData = data.length > 0 && effectiveSeriesList.length > 0;

  return (
    <div ref={containerRef} className="w-full">
      <TimeSeriesActionBar
        allowSeriesToggle={allowSeriesToggle}
        allowExport={allowExport}
        seriesMenuItems={seriesMenuItems}
        visibleKeySet={visibleSeriesKeySet}
        onToggleSeries={toggleSeriesVisibility}
        exportFormats={resolvedExportFormats}
        onExport={handleExport}
        onCopyImage={handleCopyImage}
        hasChartData={hasChartData}
      />
      <ChartContainer
        config={chartConfig}
        className="w-full rounded-[18px]"
        style={{ height: `${resolvedChartHeight}px` }}
      >
        <AreaChart
          data={chartData}
          margin={{
            top: 18,
            right: chartRightMargin + 4,
            bottom: xAxisTickLayout.bottomMargin + 2,
            left: 2,
          }}
        >
          <CartesianGrid
            {...resolveVizCartesianGridProps({
              axisStyle,
              yAxisId: useDualAxis ? "value" : undefined,
            })}
          />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatXAxisTick}
            interval={xAxisTickConfig.interval}
            minTickGap={xAxisTickConfig.minTickGap}
            tickMargin={Math.max(
              xAxisTickLayout.tickMargin,
              axisStyle.tickMargin,
            )}
            angle={xAxisTickLayout.angle}
            textAnchor={xAxisTickLayout.textAnchor}
            height={xAxisTickLayout.height}
            tickLine={resolveVizAxisTickLineProps()}
            axisLine={resolveVizAxisLineProps()}
            tick={
              rotateXAxis
                ? {
                    dx: xAxisTickLayout.tickDx,
                    dy: xAxisTickLayout.tickDy,
                    fontSize: axisStyle.tickFontSize,
                  }
                : { fontSize: axisStyle.tickFontSize }
            }
          />
          {useDualAxis ? (
            <>
              <YAxis
                yAxisId="value"
                tickFormatter={(value) => formatValueTick(value)}
                tickMargin={axisStyle.tickMargin}
                width={leftYAxisWidth}
                tickLine={resolveVizAxisTickLineProps()}
                axisLine={resolveVizAxisLineProps()}
                tick={{ fontSize: axisStyle.tickFontSize }}
                reversed={isPrimaryYAxisReversed}
              />
              <YAxis
                yAxisId="percent"
                orientation="right"
                tickFormatter={(value) => formatPercentTick(value)}
                tickMargin={axisStyle.tickMargin}
                width={rightYAxisWidth}
                tickLine={resolveVizAxisTickLineProps()}
                axisLine={resolveVizAxisLineProps()}
                tick={{ fontSize: axisStyle.tickFontSize }}
                reversed={isSecondaryYAxisReversed}
              />
            </>
          ) : (
            <YAxis
              tickFormatter={(value) =>
                !useNormalizedAxis && hasPercentSeries && !hasValueSeries
                  ? formatPercentTick(value)
                  : formatValueTick(value)
              }
              tickMargin={axisStyle.tickMargin}
              width={leftYAxisWidth}
              tickLine={resolveVizAxisTickLineProps()}
              axisLine={resolveVizAxisLineProps()}
              tick={{ fontSize: axisStyle.tickFontSize }}
              domain={useNormalizedAxis ? [0, 100] : undefined}
              reversed={isPrimaryYAxisReversed}
            />
          )}
          {resolvedReferenceLines.map((line) => (
            <ReferenceLine
              key={line.key}
              y={line.value}
              yAxisId={resolveReferenceYAxisId(line.axis)}
              {...resolveVizSemanticReferenceLineProps({ color: line.color })}
              label={resolveVizReferenceLabelProps({
                label: line.label,
                color: line.color,
                dy: -6,
              })}
            />
          ))}
          {resolvedReferenceAreas.map((area) => (
            <ReferenceArea
              key={area.key}
              y1={area.from}
              y2={area.to}
              yAxisId={resolveReferenceYAxisId(area.axis)}
              fill={area.color}
              fillOpacity={area.opacity}
              label={resolveVizReferenceLabelProps({
                label: area.label,
                color: area.color,
                position: "insideTopLeft",
                dx: 8,
                dy: 6,
              })}
            />
          ))}
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelKey={xKey}
                labelClassName={
                  resolvedTooltipMode === "compact" ? "text-[11px]" : undefined
                }
                nameKey="dataKey"
                labelFormatter={(value) =>
                  formatVizTooltipDomainLabel(value, i18n)
                }
                formatToPercentFields={tooltipPercentFields}
                allowedKeys={axisSeriesKeys}
                orderKeys={axisSeriesKeys}
                valueFormatter={formatTooltipValue}
                resolveScaledValue={useNormalizedAxis}
              />
            }
          />
          {legendPayload.length > 0 ? (
            <ChartLegend
              content={(props) => (
                <AiChartLegendContent
                  className={legendLayout.className}
                  verticalAlign={props.verticalAlign}
                  payload={legendPayload}
                  visibleKeySet={visibleSeriesKeySet}
                  onToggleItem={
                    allowSeriesToggle !== false
                      ? toggleSeriesVisibility
                      : undefined
                  }
                />
              )}
              verticalAlign="bottom"
              height={legendLayout.height}
              wrapperStyle={legendLayout.wrapperStyle}
            />
          ) : null}
          {effectiveSeriesList.map((entry, index) => {
            const color =
              chartConfig[entry.key]?.color ??
              resolveVizSeriesColor({
                index,
                color: entry.color,
                paletteMode: resolvedPaletteMode,
              });
            const yAxisId = useDualAxis
              ? percentKeySet.has(entry.key)
                ? "percent"
                : "value"
              : undefined;
            return (
              <Area
                key={entry.key}
                type={resolveTimeSeriesLineInterpolation({
                  dataKey: entry.key,
                  fieldMetaMap: metaByName,
                })}
                dataKey={entry.key}
                name={resolveDisplayLabel(
                  seriesLabels?.[entry.key],
                  entry.label,
                  metaByName.get(entry.key)?.label,
                  entry.key,
                )}
                stroke={color}
                fill={color}
                fillOpacity={0.22}
                strokeWidth={2.25}
                strokeOpacity={0.96}
                dot={resolveTimeSeriesStaticDotProps({
                  finitePointCount: finitePointCountBySeriesKey.get(entry.key),
                  color,
                  radius: isMobile ? 2.5 : 3,
                })}
                activeDot={resolveVizActiveDotProps({
                  radius: isMobile ? 3 : 4,
                  color,
                })}
                connectNulls
                stackId={stacking === "stacked" ? "viz-stack" : undefined}
                yAxisId={yAxisId}
                isAnimationActive={false}
                animationBegin={0}
                animationDuration={0}
              />
            );
          })}
        </AreaChart>
      </ChartContainer>
    </div>
  );
}
